import { useEffect, useMemo, useState } from 'react';
import { Col, notification, Row } from 'antd';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { Button } from 'components';
import { EditIcon } from 'icons';
import { EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useApi } from 'hooks/useApi';
import FinanceInvoices from 'libs/api/finance-invoices';
import { useRecoilValue } from 'recoil';
import { getPage, getShoppingCenters } from 'hooks/useUser';
import { getShops, getInactiveShops } from 'hooks/useShops';
import { financeInvoicesAdapter } from './invoices.adapter';
import UIHelper from '../../../../helpers/UIHelper';
import moment from 'moment';

const api = new FinanceInvoices('finance/invoices/blank_invoices');
const financeInvoicesApi = new FinanceInvoices();

const useInvoices = (isMainPage, isBlank = false) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [sp, setSearchParams] = useSearchParams();
  const shoppingCenters = useRecoilValue(getShoppingCenters);
  const pages = useRecoilValue(getPage);
  const shops = useRecoilValue(getShops);
  const inactiveShops = useRecoilValue(getInactiveShops);
  const groupBy = sp.get('group_by');
  const includeCompany = sp.get('include_company');
  const [modalVisible, setModalVisible] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [comment, setComment] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const {
    data,
    updateInProcess,
    loading,
    onChangeTableFilter,
    handleDownloadExcel,
    onUpdate,
    singleItem,
    setLoading,
    searchParams,
    fetchList,
  } = useApi({
    resourceKey: 'invoices',
    singleResourceKey: 'invoices',
    api,
    isMainPage,
    adapter: financeInvoicesAdapter,
    aditionalFilters: undefined,
  });

  const statusTexts = useMemo(
    () => ({
      confirm: t('Confermato'),
      created: t('Nuovo'),
      reject: t('Rifiutato'),
    }),
    [t],
  );
  const handleApprove = (invoiceId, companyId) => {
    setCurrentAction('approve');
    setSelectedInvoiceId(invoiceId);
    setSelectedCompanyId(companyId);
    setModalVisible(true);
  };

  const handleReject = (invoiceId, companyId) => {
    setCurrentAction('reject');
    setSelectedInvoiceId(invoiceId);
    setSelectedCompanyId(companyId);
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const status = currentAction === 'approve' ? 'confirm' : 'reject';
      const result = await financeInvoicesApi.approveInvoice(
        selectedInvoiceId,
        selectedCompanyId,
        comment,
        status,
      );
      fetchList();
    } catch (error) {
      console.log('Error approving/rejecting invoice');
    }
    setModalVisible(false);
    setComment('');
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setComment('');
  };

  const columns = useMemo(() => {
    return [
      {
        title: t('Date'),
        dataIndex: 'invoice_date',
        fixed: 'left',
        width: 180,
        sorter: () => {},
        render: (row) => {
          switch (groupBy) {
            case 'day':
              return UIHelper.formatedDate(row, 'DD MMMM YYYY');
            case 'week':
              return `${t('Week')} ${moment(
                row,
              ).isoWeek()}, ${UIHelper.formatedDate(row, 'YYYY')}`;
            case 'month':
              return UIHelper.formatedDate(row, 'MMMM YYYY');
            case 'year':
              return UIHelper.formatedDate(row, 'YYYY');
            default:
              return `${t('Week')} ${moment(
                row,
              ).isoWeek()}, ${UIHelper.formatedDate(row, 'YYYY')}`;
          }
        },
      },
      {
        title: t('Outlet Village'),
        dataIndex: 'shopping_center_title',
        sorter: () => {},
      },
      {
        title: t('Insegna'),
        dataIndex: 'company_name',
        sorter: () => {},
        render: (record) => (record ? record : ''),
   
      },
      {
        title: `${t('NETTO')} (€)`,
        dataIndex: 'taxable_sum',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: `${t('LORDO')} (€)`,
        dataIndex: 'taxable_vat_sum',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: t('SCONTRINI'),
        dataIndex: 'receipts_sum',
        sorter: () => {},
      },
      {
        title: t('PEZZI'),
        dataIndex: 'pieces_sum',
        sorter: () => {},
      },
      {
        title: t('Ingressi PdV'),
        dataIndex: 'visitors_sum',
        sorter: () => {},
      },
      //https://kuzevanoff.atlassian.net/jira/software/projects/AR/boards/13?assignee=5aa18678032612267be00884&selectedIssue=AR-645
      // {
      //   title: t('Footfall village'),
      //   dataIndex: 'footfall_village',
      //   // TODO when will be added sorting, uncomment this line
      //   // sorter: () => {},
      // },
      {
        title: `${t('ATV')} €`,
        dataIndex: 'atv_sum',
        sorter: () => {},
        render: (r) => `${r}`,
      },
      {
        title: t('UPT'),
        // dataIndex: 'upt_sum',
        dataIndex: 'upt_sum',
        sorter: () => {},
      },
      {
        title: t('CR PDV'),
        dataIndex: 'conversion_rate_sum',
        sorter: () => {},
        render: (r) => `${r || 0} %`,
      },
      // https://kuzevanoff.atlassian.net/jira/software/projects/AR/boards/13?assignee=5aa18678032612267be00884&selectedIssue=AR-645
      // {
      //   title: t('CR Village'),
      //   dataIndex: 'cr_village',
      //   render: (r) => `${r || 0} %`,
      // },
      {
        title: t('Chiusura Sett.'),
        dataIndex: 'week_closed',
        sorter: () => {},
        render: (value) => {
          return value ? t('Yes') : t('No');
        },
      },

      {
        title: '',
        dataIndex: 'address',
        align: 'right',
        width: 160,
        fixed: 'right',
        render: (data, row) =>
          (groupBy === 'week' || !groupBy) &&
          (pages?.ability?.can_read !== false ||
            row.can_read !== false ||
            row.can_update !== false) && (
            <Row
              gutter={16}
              justify='end'
              onClick={(e) => e.stopPropagation()}
            >
              {(pages?.ability?.can_read !== false ||
                row.can_read !== false) && (
                <Col>
                  <Link to={`${pathname}/${row.id}`}>
                    <Button type={'secondary'} icon={<EyeOutlined />} />
                  </Link>
                </Col>
              )}
              {row.can_update !== false && (
                <Col>
                  <Link to={`${pathname}/${row.id}/edit`}>
                    <Button type={'secondary'} icon={<EditIcon />} />
                  </Link>
                </Col>
              )}
            </Row>
          ),
      },
    ];
    // .filter((i) => {
    //   if (i.dataIndex === 'company_name') {
    //     return includeCompany === '1';
    //   }

    //   return true;
    // });
  }, [pathname, t, groupBy, includeCompany, pages?.ability?.can_read]);

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'shopping_center_id',
        values: shoppingCenters?.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'company',
        values: shops?.map((i) => ({
          value: i.id,
          title: i.title,
        })),
        inactiveValues: inactiveShops?.map((i) => ({
          value: i.id,
          title: i.title,
        })),
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'date_from',
        keys: ['date_from', 'date_to'],
      },
      {
        title: t('Periodo'),
        type: 'select',
        name: 'group_by',
        values: [
          { value: '', title: 'Raggruppamento per' },
          { value: 'day', title: t('Giorno') },
          { value: 'week', title: t('Week') },
          { value: 'month', title: t('Mese') },
          { value: 'year', title: t('Anno') },
        ],
      },
      {
        title: t('INCLUDI INSEGNA'),
        type: 'checkbox',
        name: 'include_company',
        values: [{ value: 'on', title: 'test1' }],
      },
    ],
    [t, shoppingCenters, shops, inactiveShops],
  );

  const onExportTo = async () => {
    setLoading(true);
    try {
      const params = {
        ...Object.fromEntries([...searchParams]),
        export_to_csv: true,
      };
      await financeInvoicesApi.onExportToExcel(params);
    } catch (e) {
      notification.error({
        message: e?.response?.data?.base?.join(',') || 'Errore',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sp.get('group_by')) {
      setSearchParams({
        group_by: 'week',
        shopping_center_id: '',
        company: '',
      });
    }
  }, [sp, setSearchParams]);

  return {
    columns,
    data,
    filters,
    updateInProcess,
    loading,
    onChangeTableFilter,
    onUpdate,
    singleItem,
    onExportTo,
    handleDownloadExcel,
    modalVisible,
    handleModalOk,
    handleModalCancel,
    setComment,
    currentAction,
    statusTexts,
  };
};

export default useInvoices;
